<svelte:options customElement={{
  tag: 'vv-election-center'
}} />

<script context="module">
  import appCssUrl from '../../app.css?inline';
  let style = '<style' + `>${appCssUrl}</style>`;
</script>

<script lang="ts">  
  import Button from '../../lib/Button.svelte';
  import Calendar from '../../lib/Calendar.svelte';
  import * as t from '../../translations/ElectionCenter.ts';
  import { type Address, addressStore, getResolvedAddresses } from '../../api/address.ts';
  import { getDatePart, getLocalizedMonthAbbreviation, vvSettings } from '../../api/common.ts';
  import voteImgUrl from '../../assets/generated/vote2_800.avif';
  import ExternalPageIcon from '../../icons/ExternalPageIcon.svelte';
  import { getVotingResources } from '../../api/elections.ts';
  import AddressCollectionEditBox from '../../lib/AddressCollectionEditBox.svelte';
  import { tryLoadUser, type User } from '../../api/users.ts';

  const ec = vvSettings.electionCenter!;
  let selectedAddress: Address;
  let editingAddress;
  let isAddressLoading = true;
  let stateIcon = 'us';
  let urlRegisterToVote = '';
  let urlFindYourPollingLocation = '';
  let urlGetPersonalizedVotingResources = '';
  const applicationType = 'advocacy';
  
  const getDomainFromHref = (href: string) => 
    !href ? '' : new URL(href).hostname || 'External Link';
  $: resourceLinks = [
    {
      id: 'register-link',
      header: t.VotingResources_Register_Header(),
      subtitle: t.VotingResources_Register_SubTitle(),
      href: urlRegisterToVote,
      domain: getDomainFromHref(urlRegisterToVote),
    }, {
      id: 'find-link',
      header: t.VotingResources_Find_Header(),
      subtitle: t.VotingResources_Find_SubTitle(),
      href: urlFindYourPollingLocation,
      domain: getDomainFromHref(urlFindYourPollingLocation),
    }, {
      id: 'get-link',
      header: t.VotingResources_Get_Header(),
      subtitle: t.VotingResources_Get_SubTitle(),
      href: urlGetPersonalizedVotingResources,
      domain: getDomainFromHref(urlGetPersonalizedVotingResources),
    }
  ]

  let elections = ec.elections || [];
  if (elections.length) {
    fixUpElectionDates();
  }

  function fixUpElectionDates () {
    if (elections) {
      for (let i = 0; i < elections.length; i++) {
        const e = elections[i];
        if (!e.year || !e.month || !e.day) {
          var dateStr = e.generalElectionDate
            || e.primaryRunOffDate
            || e.primaryElectionDate;
          if (dateStr) {
            e.year = getDatePart(dateStr, 'year');
            e.month = getLocalizedMonthAbbreviation(dateStr);
            e.day = getDatePart(dateStr, 'day');
            // Get tomorrow
            const currentDate = new Date();
            currentDate.setHours(0);
            currentDate.setMinutes(0);
            currentDate.setSeconds(0);
            currentDate.setDate(currentDate.getDate() + 1);
            const passed = new Date(dateStr) <= currentDate;
            e.passed = passed;
          }
        }
      }
    }
  }

  async function _getVotingResources(selectedAddress) {
    var votingResources = await getVotingResources({
        streetAddress: selectedAddress?.streetAddress,
        city: selectedAddress?.city,
        state: selectedAddress?.state,
        zipCode: selectedAddress?.zipCode,
				zipCodeExtension: selectedAddress?.zipCodeExtension
    }, selectedAddress?.state ?? "");
    urlRegisterToVote = votingResources.VoterRegistrationUrl || '';
    urlFindYourPollingLocation = votingResources.PollingLocationUrl || '';
    urlGetPersonalizedVotingResources = votingResources.AdditionalResourcesUrl || '';
    elections = votingResources.Elections;
    fixUpElectionDates();
  }
  
  $: isValidAddress = !!$addressStore.selectedAddress?.checksum;

  function getQueryObj(selectedAddress) {
    const addressType =  selectedAddress?.addressType === 'B' ? 'Business' : 'Home'
    return {
      addressType:addressType,
      checksum: selectedAddress?.checksum ?? "",
      streetAddress: selectedAddress?.streetAddress ?? "",
      city: selectedAddress?.city ?? "",
      latitude: selectedAddress?.coordinates?.latitude?.toString() ?? "",
      longitude: selectedAddress?.coordinates?.longitude?.toString() ?? "",
      country: selectedAddress?.country ?? "",
      county: selectedAddress?.county ?? "",
      state: selectedAddress?.state ?? "",
      zipCode: selectedAddress?.zipCode ?? "",
      zipCodeExtension: selectedAddress?.zipCodeExtension ?? "",
    };
  }
  $: urlViewOfficialsWithAddress = isValidAddress 
    ? `${ec.urlViewOfficialsWithAddress}?${new URLSearchParams(getQueryObj($addressStore.selectedAddress)).toString()}`
    : ec.urlViewOfficials;
  $: urlMyCandidates = isValidAddress
    ? `${ec.urlMyCandidates}?${($addressStore.selectedAddress?.addressType === 'B' ? 'Business' : 'Home')}=${encodeURIComponent(JSON.stringify(getQueryObj($addressStore.selectedAddress)))}`
    : ec.urlMyCandidates;

  async function onSelectOrClearAddress() {
    urlRegisterToVote = '';
    urlFindYourPollingLocation = '';
    urlGetPersonalizedVotingResources = '';

    if (selectedAddress?.checksum) {
      $addressStore.selectedAddress = selectedAddress;
      $addressStore.addresses.set(selectedAddress.addressType, selectedAddress);
    }

    await _getVotingResources($addressStore.selectedAddress);
  }
  
  
  async function load() {
    const searchParams = new URLSearchParams(document.location.search);
    const userInfo = await tryLoadUser();
    const addressCollection = await getResolvedAddresses(searchParams, userInfo, applicationType);
    const selectedAddress = addressCollection.selectedAddress;
    $addressStore.selectedAddress = addressCollection.selectedAddress;
    $addressStore.addresses = addressCollection.addresses;

    isAddressLoading = false;
    editingAddress = !selectedAddress?.checksum;

    if (selectedAddress && (selectedAddress?.state == ec.address?.state)) {
      urlRegisterToVote = ec.urlRegisterToVote ?? '';
      urlFindYourPollingLocation = ec.urlFindYourPollingLocation ?? '';
      urlGetPersonalizedVotingResources = ec.urlGetPersonalizedVotingResources ?? '';
    }
    if (selectedAddress) {
      _getVotingResources($addressStore.selectedAddress);
    }
  }

  $: stateIcon = $addressStore.selectedAddress?.state?.toLowerCase() || 'us'

  load();
</script>

<!-- This imports the styles into the web component. -->
{@html style}

<div class="@container w-full m-auto max-w-[1000px] py-2 px-8 md:px-2 flex flex-col gap-8 interfont">
  <!-- Top Section -->
  <div class="flex flex-col @xl:flex-row gap-4">
    <div class="flex-1"> 
      <h1 class="text-4xl mt-1">{t.Page_Header()}</h1>
      <div class="mt-2 mb-8">{t.Page_SubTitle()}</div>
      <div class="border-solid border-t-1 border-gray rounded p-2 ">
        <div class="text-xl mb-4">{t.Calendar_Header()}</div>
        <div class="gap-4 flex flex-col">
          {#if elections.length}
          {#each elections as e}
            <Calendar year={e.year} month={e.month} day={e.day} description={e.election} displayCategory={e.displayCategory}
             passed={e.passed}></Calendar>
          {/each}            
          {:else}
            No upcoming events
          {/if}          
        </div>
      </div>
    </div>
    <div class="flex-1 flex flex-col">
      <AddressCollectionEditBox
              bind:editingAddress
              bind:selectedAddress
              onSelectAddress={onSelectOrClearAddress}
              onClearAddress={onSelectOrClearAddress}
              isLoading={isAddressLoading}
      >
        {t.EmptyAddress_Header()}
      </AddressCollectionEditBox>
      {#if !isAddressLoading && !editingAddress}
        <div class="flex-1"></div>
        <span id="stateIcon" class="text-center w-full !text-[170px] leading-none
          vv-state-icon stateface stateface-{stateIcon}
          text-[var(--vv-action-center-association-color,var(--vv-black))]">
        </span>
        <div class="flex-1"></div>
      {/if}
    </div>
  </div>
  <!-- Custom Section -->
  {#if ec.customSectionHeader || ec.customSectionBody}
  <div class="">
    {#if ec.customSectionHeader}
    <h2 class="text-2xl">
      {@html ec.customSectionHeader}
    </h2>
    {/if}
    {#if ec.customSectionBody}
    <div class="mt-1">
      {@html ec.customSectionBody}
    </div>
    {/if}
  </div>
  {/if}
  <!-- Voting Resources -->
  <div class="">    
    <h2 class="text-2xl">{t.VotingResources_Header()}</h2>
    <div class="mt-1">{t.VotingResources_SubTitle()}</div>
  </div>
  <div class="flex flex-col grid-cols-3 @xl:grid gap-4">
    {#each resourceLinks as rl}
    <div class="border-l-4 border-solid pl-4 flex-1 flex flex-col grow-0
      border-[var(--vv-action-center-association-color,var(--vv-grey-power))]">
      <div class="text-lg font-bold ">{rl.header}</div>
      <div>{rl.subtitle}</div>
      <div class="flex-1"></div>
      {#if rl.href}
      <div class="mt-2">
          <Button data-test-id={rl.id} tag="a" type="secondary" href={rl.href} target="_blank" class="inline-flex flex-row items-center !no-underline w-full justify-center"
              title="External Page">
              <div class="text-nowrap text-ellipsis overflow-hidden">{rl.domain}</div>
              &nbsp;              
              <ExternalPageIcon class="text-[var(--vv-action-center-button-color)] ml-0.5" />
          </Button>
      </div>
      {/if}
    </div>
    {/each}
  </div>
  <!-- My Elected Officials and Candidates -->
  <div class="flex flex-col @xl:flex-row gap-8">
    <div class="flex-1 flex flex-col gap-8 justify-center items-center">
      <div class="flex flex-col gap-6 w-full" data-test-id="My Elected Officials">
        <div class="text-xl">{t.MyElectedOfficials_Header()}</div>
        <div>{t.MyElectedOfficials_Body()}</div>
        <Button data-test-id="View Officials" tag="a" type={selectedAddress ? "action-center" : "secondary"}
          class="w-full !no-underline flex justify-center items-center" 
          href={urlViewOfficialsWithAddress}>
            {t.MyElectedOfficials_ViewButton()}
        </Button>
      </div>
      <div class="w-full bg-gray-200 h-[2px]">&nbsp;</div>
      <div class="flex flex-col gap-6 w-full" data-test-id="My Candidates">
        <div class="text-xl">{t.MyCandidates_Header()}</div>
        <div>{t.MyCandidates_Body()}</div>        
        <Button data-test-id="View Candidates" tag="a" type={selectedAddress ? "action-center" : "secondary"}
          class="w-full !no-underline flex justify-center items-center" 
          href={urlMyCandidates}>
            {t.MyCandidates_ViewButton()}
        </Button>
      </div>
    </div>
    <div class="flex-[2_2_0%] flex items-center justify-center w-full min-h-[200px]">
      <img src={ec.imageUrl || voteImgUrl} alt="vote" title="vote" class="object-cover w-full h-full" /> 
    </div>
  </div>  
</div>

<style>      
  /* Importing stateface styles here instead of app.css, because it might only be used on this page. */
  @import '../../assets/stateface/stateface.css';  
  /* .content-grid {
    --padding-inline: 2rem;
    --content-max-width: 70ch;
    grid-template-columns: 
      [full-width-start] minmax(var(--padding-inline), 1fr)      
      [content-start] min(100% - (var(--padding-inline)*2), var(--content-max-width)) [content-end] 
      minmax(var(--padding-inline), 1fr) [full-width-end];
  } */
  /* .content-grid > * {
    grid-column: content;
  } */
</style>